import React, { useState, useEffect, useRef, useContext } from 'react';
import { Helmet } from "react-helmet";
import { useHistory } from 'react-router-dom';
import useNetwork from '../../useNetwork';
import { CountdownTimer } from './CountdownTimer';
import { urlEncodeText, computeSystemTimeDifference, getSecondsToTime } from '../../helpers';
import { Modal } from 'bootstrap';
import { BidDialog } from './BidDialog';
import { AppDataContext, VLink } from "../AppDataContext";
import CurrencyFormat from 'react-currency-format';
import CurrencyInput from 'react-currency-input-field';
import { Loader } from '../Loader';
import ImageGallery from 'react-image-gallery';
import LoginAndRegistration from '../LoginAndRegistration';
import { Tooltip } from 'bootstrap'


export function Lot(props) {
	const context = useContext(AppDataContext);
	const history = useHistory();

	// this will determine how many seconds different the browser is from the system
	// we will add it back on the computation of the time to close in order to match the
	// system clock more closely, if not exactly
	const systemSecondsToCloseDelta = useRef(0);
	const [secondsToClose, setSecondsToClose] = useState(null);

	const timer = useRef(null);
	const lotRef = useRef(null);
	const [lot, setLot] = useState(null);

	const [currentBid, setCurrentBid] = useState(null);
	const [inputValue, setInputValue] = useState('');
	const [wasValidated, setWasValidated] = useState(false);
	const [pendingBidAmount, setPendingBidAmount] = useState(null);
	const [isFavorite, setIsFavorite] = useState(false);
	const [hasBid, setHasBid] = useState(false);
	const [lotImages, setLotImages] = useState([]);

	const toggleFavorite = async () => {
		// initially toggle to the new state
		const currentlyIsFavorite = isFavorite;
		setIsFavorite(!isFavorite);

		const url = 'api/site/setfavorite';
		const postData = {
			secureId: lot.secureId,
			isFavorite: !isFavorite,
		};

		const response = await netRequest(url, postData);

		if (response.data != null) {
			// failed?  put it back
			setIsFavorite(currentlyIsFavorite);
		}
	}

	const [showReadOnlyTerms, setShowReadOnlyTerms] = useState(false);

	const onApproved = () => {
		Modal.getInstance("#registerAuctionModal").hide();
		fetchLot();
	};

	const registerLoggedIn = (loginResponse) => {
		// when we come back from login, if we're already registered for this auction,
		// then we can just close the dialog
		if (loginResponse.currentRegisteredAuctions.find(reg => reg.secureAuctionId == lot.auctionSession.auction.secureId))
			Modal.getInstance("#registerAuctionModal").hide();
		fetchLot();
	};

 	const { loading, networkError, netRequest, netRenderError } = useNetwork(true);


	const calcMinimumBid = (startPrice, currentBid) => {
		let minimumBid = startPrice;
		if (currentBid) {
			minimumBid = currentBid.currentAmount + currentBid.incrementAmount;
			// if this user's current max IS what would be computed as the current minimum,
			// then require them to go up, yet again, to make a new bid
			if (currentBid.currentMaxAmount == minimumBid) {
				minimumBid = minimumBid + currentBid.incrementAmount;
			}
		}
		return minimumBid;
	}


	const newBidPlaced = (bid, isBroadcast) => {

		// update this page with a bid broadcast or as the result of a Place Bid
		if (props.match.params.lotId === bid.lotSecureId) {
			if (bid.bidResultEnum == "LotWidthdrawn" || bid.bidResultEnum == "LotAdded") {
				// special case, not really a bid, but using the bid broadcast to notify anyone watching that
				// the lot has been widthdrawn from or added to the auction
				console.log('Lot ' + bid.lotSecureId + ': ' + bid.bidResultEnum);
				// close the bid dialog if it is open
				Modal.getInstance("#placeBidModal")?.hide();
				fetchLot();
			} else if (isBroadcast && bid.paddleNumber == lotRef.current.auctionSession.auction.paddleNumber) {
				// if the bid paddle number from a broadcast coming in is the same as this user's paddle number, then
				// we just got a broadcast from a different tab/browser, or registration is complete, and the pending bids are coming in...
				// so the easiest thing to do us update the view
				setHasBid(true);
				fetchLot();
			} else if (bid.bidResultEnum == "NotApproved") {
				setPendingBidAmount(bid.pendingBidAmount);
			} else {
				systemSecondsToCloseDelta.current = computeSystemTimeDifference(bid.closeTime, bid.secondsToClose, new Date());

				// update the clock
				const localSecondsToClose = getSecondsToTime(bid.closeTime, new Date(), systemSecondsToCloseDelta.current);
				setSecondsToClose(localSecondsToClose);

				bid.isBroadcast = isBroadcast;
				setCurrentBid(bid);
				if (!isBroadcast)
					setHasBid(true);
				setBidAmount(bid.currentAmount + bid.incrementAmount);
				setMinimumBid(calcMinimumBid(lot.startPrice, bid));
				setInputValue('');
				bidInputRef.current?.setCustomValidity('');
				setWasValidated(false);
				lotRef.current.closeTime = bid.closeTime;
			}
		}
	};

	const newBidBroadcastRef = useRef(null);
	const newBidBroadcast = (event) => {
		newBidPlaced(event.detail, true);
	};

	const tick = () => {
		// set up timer
		timer.current = setTimeout(() => {			
			if (lotRef.current != null) { 
				const localSecondsToClose = getSecondsToTime(lotRef.current.closeTime, new Date(), systemSecondsToCloseDelta.current);
				setSecondsToClose(localSecondsToClose);
			}
			tick();
			
		}, 1000);
	}

	// this useEffect has [] as the parms, so only gets call on didMount
	useEffect(() => {

		// kick off the countdown timer
		tick();

		// clean up if this effect gets called again
		return () => {
			// remove timer
			if (timer.current) {
				console.log('clearing timer');
				clearTimeout(timer.current);
			}
		};
	}, []);

	useEffect(() => {
		

		if (lot) {

			document.querySelectorAll('[data-bs-toggle = "tooltip"]').forEach(el => {
				new Tooltip(el);
			});

			window.scrollTo(0, 0);
			const localSecondsToClose = getSecondsToTime(lot.closeTime, new Date(), systemSecondsToCloseDelta.current);

			if (context.hubConnected) {
				if (localSecondsToClose > 0) {
					newBidBroadcastRef.current = newBidBroadcast;
					context.subscribeToBids(lot.secureId, newBidBroadcastRef.current);
				}
			} else {
				console.log("Hub not up yet, subscribing to bids later")
            }
			lotRef.current = lot;
			setIsFavorite(lot.isFavorite);
			setHasBid(lot.hasBid);
			setInputValue('');
			bidInputRef.current?.setCustomValidity('');
			setWasValidated(false);

			let images = [];

			for (var i = 0; i < lot?.inventoryItemImageSecureIds.length; i++){
				let image = {
					original: 'api/site/InventoryImage/' + lot?.inventoryItemImageSecureIds[i] + '/450',
					fullscreen: 'api/site/InventoryImage/' + lot?.inventoryItemImageSecureIds[i] + '/1000',
					thumbnail: 'api/site/InventoryImage/' + lot?.inventoryItemImageSecureIds[i] + '/450', // we seem to get better performance loading just a single 450 image, loading 45px images really slows down all of the images coming down from server
					thumbnailLoading: 'lazy',
					loading: 'eager',
					thumbnailWidth: '45px',
					thumbnailHeight: '45px',
					 
				};
				images.push(image);
			}
			setLotImages(images);			
		}
		return () => {
			if (newBidBroadcastRef.current != null) {
				// stop watching bids for the previously viewed lot
				context.unsubscribeToBids(lotRef.current.secureId, newBidBroadcastRef.current);
				newBidBroadcastRef.current = null;
			}
		};
	}, [lot]);

	useEffect(() => {
		if (context.hubConnected && lotRef.current != null) {
			if (newBidBroadcastRef.current == null) {
				const localSecondsToClose = getSecondsToTime(lotRef.current.closeTime, new Date(), systemSecondsToCloseDelta.current);
				if (localSecondsToClose > 0) {
					newBidBroadcastRef.current = newBidBroadcast;
					context.subscribeToBids(lotRef.current.secureId, newBidBroadcastRef.current);
				}
			}
			console.log("Hub Connected, refreshing lot");
			fetchLot();
		}
		if (context.hubConnected == false && newBidBroadcastRef.current != null && lotRef.current != null) {
			context.unsubscribeToBids(lotRef.current.secureId, newBidBroadcastRef.current);
			newBidBroadcastRef.current = null;
		}
	}, [context.hubConnected]);


	// this useEffect looks for changes in the lot Id parameter
	useEffect(() => {
		// clearing lot images here otherwise if done in the lot effect state doesn't update in time and previous images show in the lightbox carousel 
		// for the next lot prior to new images loading.
		setLotImages([]);

		fetchLot();
	}, [props.match.params.lotId, context.isLoggedIn, context.registeredAuctionsChanged]);

	function fetchLot() {
		const fetchLotAsync = async () => {
			const url = 'api/site/getlot/' + props.match.params.lotId;

			const response = await netRequest(url);

			if (response.data != null) {

				setLot(response.data);
				setPendingBidAmount(response.data.pendingBidAmount);

				if (response.data.auctionSession.auction.isRegistered) {
					context.setRegisteredAuction(response.data.auctionSession.auction.secureId, response.data.auctionSession.auction.paddleNumber);
				}

				// update the system lag delta so it should stay accurate
				systemSecondsToCloseDelta.current = computeSystemTimeDifference(response.data.closeTime, response.data.secondsToClose, new Date());

				const localSecondsToClose = getSecondsToTime(response.data.closeTime, new Date(), systemSecondsToCloseDelta.current);
				setSecondsToClose(localSecondsToClose);

				setCurrentBid(response.data.currentBid);
				setHasBid(response.data.hasBid);
				setBidAmount(response.data.currentBid == null ? response.data.startPrice : response.data.currentBid.currentAmount + response.data.currentBid.incrementAmount);
				setMinimumBid(calcMinimumBid(response.data.startPrice, response.data.currentBid));

				// require the "optional text" tacked on the end of the url to be only one thing
				// this is supposed to be for SEO.  Since it just changes the URL, and does not send the
				// browser an 301 from the server, it is questionable as to whether or not it will work for SEO
				const encodedName = urlEncodeText(response.data.title);
				if (props.match.params.optionalTitle != encodedName) {
					history.replace('/Auction/' + props.match.params.auctionId + '/Lot/' + props.match.params.lotId + '/' + encodedName)
				}

			}
		};

		fetchLotAsync();
	}

	// set the state to open the dialog so now
	// the useEffect watching openBidDialog will actually find the modal in the dom
	// i use a random number for the reinitialize property on the dialog component
	// so that we can change it, and the dialog will reset any state vars before opening
	const [reinitializeBidDialog, setReinitializeBidDialog] = useState(Math.random());

	const [openBidDialog, setOpenBidDialog] = useState(false);
	const [bidAmount, setBidAmount] = useState(1);
	const [minimumBid, setMinimumBid] = useState(1);

	const bidInputRef = useRef();

    const updateBidValue = (value) => {
        if (!value) {
            setBidAmount(1);
            setInputValue('');
        } else {
            setBidAmount(value);

            setInputValue(value);
		}
		bidInputRef.current.setCustomValidity('');
	}

	const onSubmitBidForm = async (event) => {
		event.preventDefault();
		await showBidDialog(event);
	}

	const showBidDialog = async (event) => {
		event.preventDefault();

		setWasValidated(true);
		if (inputValue < minimumBid) {
			bidInputRef.current.setCustomValidity('invalid');
		} else {
			bidInputRef.current.setCustomValidity('');
			if (inputValue != '' && inputValue >= minimumBid) {
				setOpenBidDialog(true);
			}
		}
    }

	const [biddingHistory, setBiddingHistory] = useState(null);
	const loadBiddingHistory = async () => {
		setBiddingHistory(null);

		const url = 'api/site/getlotbids/' + lot.secureId;

		const response = await netRequest(url, null);

		if (response.data != null) {
			var lastDate = null;
			for (var ix = 0; ix < response.data.length; ix++) {
				if (response.data[ix].bidDateStr == lastDate) {
					response.data[ix].bidDateStr = null;
				} else {
					lastDate = response.data[ix].bidDateStr;
				}
            }
			setBiddingHistory(response.data);
		} else {
			// failed?  just say empty
			setBiddingHistory([]);
        }

    }

	useEffect(() => {
		if (openBidDialog) {
			setReinitializeBidDialog(Math.random());
			var myModal = new Modal("#placeBidModal", {});
			myModal.show();
		}
		setOpenBidDialog(false);
	}, [openBidDialog]);

	const closeDialog = () => {
		Modal.getInstance("#registerAuctionModal").hide();
	};

	//if (networkError) {
	//	return netRenderError();
	//}

	//if (lot == null) {
	//	return (
	//		<Loader loading={true} fullscreen={true} />
	//	);
	//}

	return (
		<main>
			<Helmet>
				<title>{lot?.title.toUpperCase()}</title>
				<meta name="robots" content="index, follow"/>
				<meta name="description" content={lot?.title.replaceAll('"', '').toUpperCase()} />
				<meta property="og:title" content={lot?.title.replaceAll('"', '').toUpperCase()} />
				<meta property="og:type" content="other"/>
				{lot?.inventoryItemImageSecureId != null &&
					<meta property="og:image" content={"https://" + window.location.hostname + '/api/site/InventoryImage/' + lot?.inventoryItemImageSecureId + '/450'} />
				}
				<meta property="og:image:width" content="450"/>
				<meta property="og:image:height" content="450"/>
				<meta property="og:description" content={lot?.description?.replaceAll('"', '')} />

			</Helmet>


			<section class="container-fluid container-md">
				<div class="row d-flex">
					<div class="col-12 col-md-6 align-self-start pt-3">
						<div class="d-flex justify-content-start mb-1 mb-md-3">
							<VLink class="lot-navigation lot-navigation__prev text-uppercase"
								to={{ pathname: '/auction/' + props.match.params.auctionId, state: props.location.state }}>{lot?.auctionTitle}
							</VLink>							
						</div>
					</div>
					<div class="col-12 d-block d-md-none">
						<h1 class="text-uppercase">{lot?.pieces && <> ({lot.pieces}pc) </>}
							{lot?.title}</h1>
						<div class="mb-2 divider"></div>
					</div>
					<div class="col-12 col-md-6 align-self-start pt-1 pt-md-3">
						<div class="d-flex justify-content-between mb-1 mb-md-3">
								{lot?.prevLotSecureId == null ?
									<VLink style={{ visibility: 'hidden' }} class="lot-navigation lot-navigation__prev" to={'/auction/' + props.match.params.auctionId}>Previous</VLink>

								:
									<VLink class="lot-navigation lot-navigation__prev" to={{ pathname: '/auction/' + props.match.params.auctionId + '/Lot/' + lot?.prevLotSecureId + '/' + urlEncodeText(lot?.prevLotTitle), state: props.location.state }}>Previous</VLink>
								}
								<span><i class="bi bi-tags"></i># {lot?.lotNumber}</span>
								{lot?.nextLotSecureId == null ?
									<VLink style={{ visibility: 'hidden' }} class="lot-navigation lot-navigation__next" to={'/auction/' + props.match.params.auctionId}>Next</VLink>
								:
									<VLink class="lot-navigation lot-navigation__next" to={{ pathname: '/auction/' + props.match.params.auctionId + '/Lot/' + lot?.nextLotSecureId + '/' + urlEncodeText(lot?.nextLotTitle), state: props.location.state }}>Next</VLink>
								}
							</div>
					</div>
					<div class="col-12 order-1 order-md-1 col-md-6 align-self-start pb-5">

						<ImageGallery items={lotImages} lazyLoad={false} useBrowserFullscreen={false} showPlayButton={false}/>



 					</div>
					<div class="col-12 col-md-6 order-2 order-md-2 align-self-start pb-5">
						<div class="lot-details">
							<h1 class="text-uppercase d-none d-md-block">
								{lot?.pieces && <> ({lot.pieces}pc) </> }
								{lot?.title}
							</h1>
							<div class="d-none d-md-block mb-3 divider"></div>
							<div class="mt-2 mt-md-0 p-3 lot-details__div background-color">
								<div class="d-flex align-items-center w-100 lot-details__div--price">
									{(context.isLoggedIn == false && secondsToClose <=0 && lot?.closeTime != null) ?
										<h4 class="me-3">
											<a class="nav-link" href="#" data-bs-toggle="modal" data-bs-target="#loginOrRegisterModal" title="Log In">Log in to see the price</a>
										</h4>
										:
										<h2 class="price me-3">
											<CurrencyFormat decimalScale={2} fixedDecimalScale={true} value={currentBid ? currentBid.currentAmount : lot?.startPrice} displayType={'text'} thousandSeparator={true} prefix={'$'} />
										</h2>
									}
									{!(currentBid && currentBid.currentMaxAmount) && hasBid &&
										<label class="me-1 mb-1 auction-label outbidded">Outbid</label>
									}

									{lot?.auctionSession != null && lot.auctionSession.auction.isRegistered && lot.auctionSession.auction.paddleNumber == null ? (
										<>
											<label class="me-1 mb-1 auction-label pending">Pending Approval</label>
											{
											pendingBidAmount != null &&
												<label class="me-1 mb-1 auction-label pending">Your Max
													&nbsp;
													<CurrencyFormat decimalScale={2} fixedDecimalScale={true} value={pendingBidAmount} displayType={'text'} thousandSeparator={true} prefix={'$'} />
												</label>
											}
										</>
										) : (
										<>
											{currentBid?.currentMaxAmount &&
												<>
													<label class="me-1 mb-1 auction-label highest">
														{secondsToClose <= -4 ? "Won!" : "High Bid"}
													</label>
													&nbsp;
													<label class="me-1 mb-1 auction-label pending">Your Max
														&nbsp;
														<CurrencyFormat decimalScale={2} fixedDecimalScale={true} value={currentBid.currentMaxAmount} displayType={'text'} thousandSeparator={true} prefix={'$'} />
													</label>
												</>
											}
										</>
										)
                                    }
								</div>
								<div class="d-block d-md-none lot-details__div--countdown">
									<div class="row pb-2">
										{lot?.isWidthdrawn ?
											<span class="col text-left badge badge-danger">Widthdrawn</span>
											:
											lot != null && lot.closeTime == null ?
												<span class="col text-center badge badge-warning">Preview</span>
											:
												secondsToClose <= -2 ?
													lot?.currentBid == null ?
														<span class="col text-center badge badge-warning">Passed Lot</span>
														:
														<span class="col text-center badge badge-warning">Sold</span>
													:
											<CountdownTimer secondsToClose={secondsToClose} />
										}
									</div>
								</div>
								{ secondsToClose > 0 &&
									<form class="row my-1 my-md-3 lot-details__div--bid needs-validation" noValidate onSubmit={onSubmitBidForm}>
										<div class="col-12 mb-3">
											<div className={"input-group input-group__inline has-validation " + (wasValidated ? 'was-validated' : '')}>

												<CurrencyInput
													required class="form-control" placeholder="Bid"
													onValueChange={updateBidValue}
													decimalsLimit={2}
													decimalScale={2}
													allowDecimals={true}
													allowNegativeValue={false}
													prefix="$"
													value={inputValue}
													ref={bidInputRef}
													disabled={lot?.closeTime == null || lot?.isWidthdrawn == true || secondsToClose <= 0}
												/>
												<div class="input-group-append">
													<button type="button" onClick={showBidDialog} class="btn btn-primary btn-fill w-100" disabled={lot?.closeTime == null || lot?.isWidthdrawn == true || secondsToClose <= 0}>Place Bid</button>
												</div>
												<div class="invalid-feedback">
													Please bid equal to or greater than the minimum.
												</div>
											</div>
											<div class="d-flex justify-content-between">
												<label>
													Minimum bid <CurrencyFormat decimalScale={2} fixedDecimalScale={true} value={minimumBid} displayType={'text'} thousandSeparator={true} prefix={'$'} />
												</label>

												{currentBid ? currentBid.bidCount : lot?.bidCount} bid{(currentBid ? currentBid.bidCount : lot?.bidCount) != 1 && "s"}
											</div>
										</div>

										<label><i class="bi bi-lock-fill"></i> Secure bid</label>

									</form>
								}
								<div class="mb-3 mx-auto text-center lot-details__div__auctioned">
									{lot &&
										<VLink to={"/seller/" + lot.auctionSession.auction.companySecureId} title={lot.auctionSession.auction.companyName}>Auctioned by
											<img src={"api/site/SellerLogo/" + lot.auctionSession.auction.companySecureId} alt={lot.auctionSession.auction.companyName} title={lot.auctionSession.auction.companyName} />
										</VLink>
									}
								</div>
								<div class="d-none d-md-block lot-details__div--countdown">
									<div class="row pt-1 pt-md-3 px-0 m-0">
										{lot?.isWidthdrawn ?
											<span class="col text-center badge badge-danger">Widthdrawn</span>
											:
											lot != null && lot.closeTime == null ?
												<span class="col text-center badge badge-warning">Preview</span>
											:
											secondsToClose <= 0 ?
												lot?.currentBid == null ?
														<span class="col text-center badge badge-warning">Passed Lot</span>
													:
														<span class="col text-center badge badge-warning">
															{secondsToClose <= -2 ? "Sold" : "Closing"}
														</span>
												:
											lot != null && lot.closeTime == null ?
												<span class="col text-center badge badge-warning">Preview</span>
												:
											<CountdownTimer secondsToClose={secondsToClose} />
										}
									</div>
								</div>
							</div>
							{lot &&
								<>
									<div class="d-block d-md-flex justify-content-between mt-3">
										{!lot.isWidthdrawn && lot.closeTime != null &&
											<>
												{context.isLoggedIn ?
													<>
														<button className={"btn btn-secondary btn-fav-lot me-3" + (isFavorite ? " active" : "")} onClick={toggleFavorite}>
															<i class="bi bi-star"></i>
															<i class="bi bi-star-fill"></i>
														</button>
														{/*<button class="btn btn-primary btn-icon me-3"><i class="bi bi-bell"></i></button>
														<button class="btn btn-primary btn-icon me-3"><i class="bi bi-share"></i></button>	*/}
													</>
													:
													<>
														<button class="btn btn-secondary btn-fav-lot me-3"  data-bs-toggle="modal" data-bs-target="#loginOrRegisterModal">
															<i class="bi bi-star"></i>
															<i class="bi bi-star-fill"></i>
														</button>
														{/*<button class="btn btn-primary btn-icon me-3" data-bs-toggle="modal" data-bs-target="#loginModal"><i class="bi bi-bell"></i></button>
														<button class="btn btn-primary btn-icon me-3" data-bs-toggle="modal" data-bs-target="#loginModal"><i class="bi bi-share"></i></button>	*/}
													</>
												}
											</>
										}
										{(context.isLoggedIn || secondsToClose > 0) &&
												<button class="btn btn-text ms-auto my-3 my-md-0" data-bs-toggle="modal"
													data-bs-target="#bidHistory" onClick={loadBiddingHistory}>Bidding History</button>	
										}
									</div>

									{
									lot.auctionSession.closed != true && lot.auctionSession.auction.isRegistered != true && lot?.closeTime != null  &&
										<p class="my-3">Get pre-approved to bid;
											&nbsp;
											<a href="#" data-bs-toggle="modal" data-bs-target="#registerAuctionModal" onClick={() => setShowReadOnlyTerms(false)} title="Register">click here</a>
											&nbsp;
											to register for this auction</p>
									}
								</>
							}
						</div>
					</div>
				</div>
				<div class="modal fade" id="bidHistory" tabindex="-1" aria-labelledby="bidHistoryLabel" aria-hidden="true">
					<div class="modal-dialog modal-dialog-centered modal-lg">
						<div class="modal-content">
							<div class="modal-header">
								<a class="logo mx-auto" href="index.html">
									<img src="images/bnd-logo@2x.png" height="" title="Bid Next Door" alt="Bid Next Door Logo" />
								</a>
								<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" ref={props.closeRef}></button>
							</div>
							<div class="modal-body">
								{
									biddingHistory == null ? <>Loading...</> :
										biddingHistory.length == 0 ? <>No Bids</> :
											<table class="w-100 bidding-history">
												<tr>
													<th>Date</th>
													<th>Time</th>
													<th>Bidder</th>
													<th class="text-end">Amount</th>
												</tr>
												{biddingHistory.map((bid) => (
													<tr>
														<td>{bid.bidDateStr}</td>
														<td style={bid.deleted ? { "text-decoration-line": "line-through" } : {}}>{bid.bidTimeStr}</td>
														<td style={bid.deleted ? {"text-decoration-line": "line-through"} : {}}>{bid.isThisUser ? "You" :
															"Paddle " + bid.paddleNumber}</td>
														<td align="right" style={bid.deleted ? { "text-decoration-line": "line-through" } : {}}><CurrencyFormat decimalScale={2} fixedDecimalScale={true} value={bid.currentAmount} displayType={'text'} thousandSeparator={true} prefix={'$'} /></td>
													</tr>
													))
												}
											</table>
								}
							</div>
						</div>
					</div>
				</div>


			</section>
			<section class="background-primary">
				<div class="container-fluid container-md">
					<div class="row pt-5">
						<div class="col-12 col-md-6 pb-5">
							<h4 class="text-left auction-grid-header">
								Item Details
								<div class="divider my-2"></div>
							</h4>
							<div class="w-100 summernote-html">
								<span class="summernote-html" dangerouslySetInnerHTML={{ __html: lot?.description }}></span>
								{lot?.fullDimensionsStr &&
									<div>
										<strong>Dimensions: </strong>
										<span>{lot?.fullDimensionsStr}</span>
									</div>

								}
								{lot?.condition &&
									<div>
										<strong>Condition: </strong>
										<span class="summernote-html" dangerouslySetInnerHTML={{ __html: lot?.condition }}></span>
									</div>
								}
								{lot?.provenance &&
									<div>
										<strong>Provenance: </strong>
										<span class="summernote-html" dangerouslySetInnerHTML={{ __html: lot?.provenance }}></span>
									</div>
								}								
							</div>
							<div>
								 
								{lot &&
										<div>
										<span><strong>Item Code:</strong> {lot.code}</span>
										</div>
									}
								 
								{lot?.transportFee &&
									<div>
										<strong>Optional Transport <i data-bs-toggle="tooltip" data-bs-title={"If you are unable to come during the pickup window, " + lot.auctionSession.auction.companyName + " will transport this item to their offices for pickup at a later time for this additional fee."} class="bi bi-info-circle-fill transport-tool-tip "></i> : </strong>
										<span><CurrencyFormat decimalScale={2} fixedDecimalScale={true} value={lot.transportFee} displayType={'text'} thousandSeparator={true} prefix={'$'} /></span>
									</div>
								}
							</div>
							<div class="d-flex align-items-center pickup__content py-2">
								<div class="d-inline me-3">
									<i class="bi bi-currency-exchange"></i>
								</div>
								<div class="d-inline">
									<p class="pb-0 mb-0">
										{lot &&
											<CurrencyFormat decimalScale={0} value={100 * lot.auctionSession.auction.buyersPremium} displayType={'text'} suffix={'%'} />
										}
										&nbsp;Buyers Premium
									</p>
								</div>
							</div>
							
						</div>

						<div class="col-12 col-md-6 pb-5">
							<h4 class="text-left auction-grid-header position-relative">
								Auction / Pickup Terms 

								<button class="btn btn-primary postion-relative mb-2 auctionTermsButton position-absolute-lg"
									onClick={() => setShowReadOnlyTerms(true)}
									data-bs-toggle="modal" data-bs-target="#registerAuctionModal" title="View more">View more</button>

								<div class="divider my-2"></div>
							</h4>
							<div class="d-flex align-items-center pickup__content py-2">
								<div class="d-inline me-3">
									<i class="bi bi-pin-map"></i>
								</div>
								<div class="d-inline">
									<h4 class="mb-0 pb-0">
										{lot?.auctionSession.auction.pickupOptionStrs.includes("PickupOnSite") ? (
											"Pickup on Location" +
											(lot?.auctionSession.auction.pickupWindowStrs.length == 1 ? " - One Day Only" : "")
										) : (
											"Shipping Only"
										)}
									</h4>
									{lot?.auctionSession.auction.pickupOptionStrs.includes("PickupOnSite") &&
										(lot?.auctionSession.auction.pickupOptionStrs.includes("RelocateStore") ||
										lot?.auctionSession.auction.pickupOptionStrs.includes("LocalDelivery") ||
										lot?.auctionSession.auction.pickupOptionStrs.includes("ThirdPartyShip") ||
										lot?.auctionSession.auction.pickupOptionStrs.includes("InHouseShip"))
										&&
										<h5 class="mb-0 pb-0">
											Alternative pickup &amp; shipping options available
										</h5>
									}
								</div>
							</div>
							<div class="d-flex align-items-center pickup__content py-2">
								<div class="d-inline me-3">
									<i class="bi bi-clock"></i>
								</div>
								<div class="d-inline">
									<h4 class="mb-0 pb-0" >{lot?.auctionSession.auction.city}, {lot?.auctionSession.auction.state}</h4>
									{lot?.auctionSession.auction.pickupWindowStrs.map((time, ix) => (
										<h5 key={"pw" + ix} class="mb-0 pb-0">{time}</h5>
									))}

								</div>
							</div>
							<div class="d-flex align-items-center pickup__content py-2">
								<div class="d-inline me-3">
									<i class="bi bi-arrow-clockwise"></i>
								</div>
								{lot?.auctionSession.auction.pickupOptionStrs.includes("ReturnsAccepted") ?
									<div class="d-inline">
										<h4 class="mb-0 pb-0">Returns Accepted</h4>
										<h5 class="mb-0 pb-0">Read the Terms for Details</h5>
									</div>

									:
									<div class="d-inline">
										<h4 class="mb-0 pb-0">No Returns</h4>
										<h5 class="mb-0 pb-0">All sales are final</h5>
									</div>
								}
							</div>
							<div class="d-flex align-items-center pickup__content py-2">
								<div class="d-inline me-3">
									<i class="bi bi-envelope"></i>
								</div>
								<div class="d-inline">
									<h4 class="mb-0 pb-0" >Contact Details</h4>
									<h5 class="mb-0 pb-0"><a href={"mailto:" + lot?.auctionSession.auction.auctionManagerEmail} title={lot?.auctionSession.auction.auctionManagerEmail}>{lot?.auctionSession.auction.auctionManagerEmail}</a> / {lot?.auctionSession.auction.auctionManagerPhone}</h5>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>
			{lot && lot.closeTime != null && lot.isWidthdrawn == false &&
				<BidDialog
				id="placeBidModal"
				lot={lot}
				onBidPlaced={newBidPlaced}
				showConfirm={true}
				reinitialize={reinitializeBidDialog}
				bidAmount={bidAmount}
				secondsToClose={secondsToClose}
			/>
			}
			
			{lot &&
				<div class="modal fade" id="registerAuctionModal" tabindex="-1" aria-labelledby="registerAuctionModalLabel" aria-hidden="true">
					<div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
						<div class="modal-content">
							<div class="modal-header">
								<div class="mx-auto text-center lot-details__div__auctioned">
									Auctioned by
									<VLink to={"/seller/" + lot.auctionSession.auction.companySecureId} title={lot.auctionSession.auction.companyName}
										onClick={closeDialog}>
										<img src={"api/site/SellerLogo/" + lot.auctionSession.auction.companySecureId} alt={lot.auctionSession.auction.companyName} title={lot.auctionSession.auction.companyName} />
									</VLink>
								</div>
								<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
							</div>
							<div class="modal-body">
								<LoginAndRegistration
									showLogin={true}
									showSignup={true}
									registrationComplete={registerLoggedIn}
									showReadOnlyTerms={showReadOnlyTerms}
									auctionSession={lot.auctionSession}
									onApproved={onApproved}
								/>
							</div>

							{!lot.auctionSession.auction.isRegistered && showReadOnlyTerms && lot.closeTime != null &&
								<div>
									<p class="my-3 text-center">Get pre-approved to bid;&nbsp;
										<a href="javascript:void(0)" onClick={() => setShowReadOnlyTerms(false)} title="Register">click here</a>;&nbsp;
										to register for this auction</p>
								</div>
							}
						</div>
					</div>
				</div>
			}
			<Loader loading={loading} fullscreen={false} />
		</main>
    );
}
